/**
 * PlanCreditsSection — PRP-MCP-9 G2: the account's plan and its two conversion
 * balances on the Settings (account) page: the plan's included conversions this
 * month (they reset at each renewal and do not carry over) and pack credits
 * (they never expire). Reads GET /me/credits. The page's own `Section` frame is
 * passed in, like McpConnectSection, so the card matches the page.
 */
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import type { CreditBalances } from '../../lib/plansApi';
import type { CreditsResponse } from '../../types';

interface FrameProps {
  title: string;
  sub?: string;
  children: ReactNode;
}

/** Display names for plan labels from GET /me/credits (tier ids). */
const PLAN_NAMES: Record<string, string> = {
  pro: 'Pro',
  pro_plus: 'Pro Plus',
  importer: 'Importer (legacy plan)',
  member: 'Team member',
  admin: 'Admin',
  free: 'Free',
};

const SUBSCRIBED_PLANS = new Set(['pro', 'pro_plus', 'importer']);

function Row({ label, value, testId }: { label: string; value: ReactNode; testId: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-[6px]">
      <span className="text-[12.5px] text-[var(--tx-lo)]">{label}</span>
      <span className="text-[13px] font-[560] text-[var(--tx-hi)]" data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

export default function PlanCreditsSection({ frame: Frame }: { frame: ComponentType<FrameProps> }) {
  const [data, setData] = useState<(CreditsResponse & CreditBalances) | null>(null);
  const [failed, setFailed] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    BackendAPI.getCredits()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const plan = data?.plan ?? 'free';
  const perMonth = data?.included_per_month ?? 0;
  const founderUntil = data?.founder_until ? new Date(data.founder_until) : null;
  // Security review M3: a plan change or cancel goes through the Stripe billing
  // portal (checkout answers 409 while a subscription is open). The server opens
  // it on the caller's own customer (M5).
  const subscribed = SUBSCRIBED_PLANS.has(plan);

  async function openPortal() {
    setPortalError(null);
    try {
      const { url } = await BackendAPI.createPortalSession();
      window.location.assign(url);
    } catch (e) {
      setPortalError(e instanceof Error ? e.message : 'Could not open the billing portal.');
    }
  }

  return (
    <Frame title="Plan and conversions" sub="What your plan includes this month, and your pack credits.">
      {failed && (
        <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">Could not load your credits. Try again later.</p>
      )}
      {!failed && !data && <p className="m-0 text-[12.5px] text-[var(--tx-faint)]">Loading…</p>}
      {data && (
        <div className="divide-y divide-[var(--bd-subtle)]">
          <Row label="Plan" value={PLAN_NAMES[plan] ?? plan} testId="plan-name" />
          {perMonth > 0 && (
            <Row
              label="Included this month"
              value={`${data.included_this_month ?? 0} of ${perMonth}`}
              testId="included-this-month"
            />
          )}
          <Row label="Pack credits (never expire)" value={data.pack_credits ?? 0} testId="pack-credits" />
          {founderUntil && (
            <Row
              label="Founding 20"
              value={`until ${founderUntil.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
              testId="founder-until"
            />
          )}
          {subscribed && (
            <div className="py-[8px]">
              <button
                type="button"
                className="text-[12.5px] font-[560] text-[var(--accent)] underline"
                onClick={openPortal}
                data-testid="manage-subscription"
              >
                Manage subscription
              </button>
              {portalError && (
                <p className="mt-1 mb-0 text-[12px] text-[var(--bad)]">{portalError}</p>
              )}
            </div>
          )}
          <p className="mt-3 mb-0 text-[11.5px] text-[var(--tx-faint)]">
            Included conversions are used first and reset each month; they do not carry over.{' '}
            <Link to="/pricing" className="text-[var(--accent)] underline">
              {plan === 'free' ? 'See the plans' : 'Plans and packs'}
            </Link>
          </p>
        </div>
      )}
    </Frame>
  );
}
